import { getSourceKey } from "#features/citations/citedContent.ts";
import { CitedSourceLink } from "#features/conversations/components/conversation-source/CitedSourceLink.tsx";
import { useConversationSource } from "#features/conversations/hooks/useConversationSource.ts";
import { useHoveredSource } from "#features/conversations/hooks/useHoveredSource.ts";
import { useSharedTranslation } from "#hooks/useSharedTranslation.ts";
import { useTracking } from "#hooks/useTracking.ts";
import { MixpanelEvent, MixpanelLinkType, MixpanelSourceLocation } from "#types/tracking.ts";
import type { ConversationSource } from "#types/types.ts";
import { useCallback } from "react";

interface Props {
    conversationId: string;
    source: ConversationSource;
    index: number;
    disabled: boolean;
}

export const AssistantMessageCitedSource = ({ conversationId, source, index, disabled }: Props) => {
    const { tShared } = useSharedTranslation();
    const { title } = useConversationSource(source);
    const { trackEvent } = useTracking();
    const { isHovered, onMouseEnter, onMouseLeave } = useHoveredSource(getSourceKey(source));

    // Validate URL - handles null and undefined
    const hasValidUrl = Boolean(source.url);

    const onClick = useCallback((event: React.MouseEvent) => {
        // If source url is not available, prevent default link behavior
        if (!hasValidUrl) {
            event.preventDefault();
            event.stopPropagation();

            return; // Early return, no tracking for invalid URLs
        }

        trackEvent(MixpanelEvent.AssistantSourceClicked, {
            Location: MixpanelSourceLocation.Inline,
            "Conversation Id": conversationId,
            Type: MixpanelLinkType.Source
        });
    }, [conversationId, hasValidUrl, trackEvent]);

    return <CitedSourceLink
        source={source}
        index={index}
        ariaLabel={hasValidUrl ? tShared("visit", { title }) : title}
        isHovered={isHovered}
        disabled={disabled}
        onClick={onClick}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
    />;
};
